// Reads finger gestures in the phone video viewer.
//
// A state machine that never touches the DOM. The caller passes the times and
// sets the timers (call `tick()` at `deadline()`), so tests can move time.
//
// - Tap: waits `doubleTapMs` for a second tap, then `tap`
// - Double tap: right half skips forward, left half back. For `seekChainMs`
//   afterwards every further tap adds 10 seconds (as on YouTube)
// - Long press: held still for `longPressMs` gives `holdStart`, release gives `holdEnd`
// - Swipe: vertical by `swipePx` or more, and more vertical than horizontal
//
// Points are in the *visual* orientation (after the 90° rotation while the
// video is turned). `toViewPoint` converts from device coordinates.

export interface GesturePoint {
  x: number;
  y: number;
  t: number;
}

export type GestureOutput =
  | { kind: "tap" }
  | { kind: "seek"; side: "back" | "forward"; total: number }
  | { kind: "holdStart" }
  | { kind: "holdEnd" }
  | { kind: "swipe"; dir: "up" | "down" };

export const GESTURE = {
  /** Wait for a second tap after the first is released. A single tap takes effect this late. */
  doubleTapMs: 250,
  /** How close a second tap must be. */
  doubleTapPx: 40,
  /** After a double tap, further taps within this (from the last release) add seconds. */
  seekChainMs: 700,
  /** How long a press must last to be a long press. */
  longPressMs: 450,
  /** Moving more than this makes it a move, not a press. */
  moveSlopPx: 12,
  /** Vertical distance of a swipe. */
  swipePx: 60,
  /** Seconds skipped per tap. */
  seekStep: 10,
} as const;

interface Press {
  start: GesturePoint;
  moved: boolean;
  held: boolean;
}

type Side = "back" | "forward";

export class VideoGestures {
  private press: Press | null = null;
  /** The tap waiting for a second one (where it was pressed, when it was released). */
  private pending: GesturePoint | null = null;
  private chain: { side: Side; total: number; until: number } | null = null;

  /** `width` is the visual width (which half is which). A function, as rotating changes it. */
  constructor(private readonly width: () => number) {}

  down(p: GesturePoint): GestureOutput[] {
    if (this.press) return [];
    this.press = { start: p, moved: false, held: false };
    return [];
  }

  move(p: GesturePoint): GestureOutput[] {
    const pr = this.press;
    if (pr && !pr.held && distance(p, pr.start) > GESTURE.moveSlopPx)
      pr.moved = true;
    return [];
  }

  up(p: GesturePoint): GestureOutput[] {
    const pr = this.press;
    if (!pr) return [];
    this.press = null;
    if (pr.held) return [{ kind: "holdEnd" }];
    if (pr.moved || distance(p, pr.start) > GESTURE.moveSlopPx) {
      const dx = p.x - pr.start.x;
      const dy = p.y - pr.start.y;
      if (Math.abs(dy) >= GESTURE.swipePx && Math.abs(dy) > Math.abs(dx)) {
        this.pending = null;
        return [{ kind: "swipe", dir: dy < 0 ? "up" : "down" }];
      }
      return [];
    }
    const side: Side = pr.start.x < this.width() / 2 ? "back" : "forward";
    if (this.chain && pr.start.t <= this.chain.until) {
      const total =
        this.chain.side === side
          ? this.chain.total + GESTURE.seekStep
          : GESTURE.seekStep;
      this.chain = { side, total, until: p.t + GESTURE.seekChainMs };
      return [{ kind: "seek", side, total }];
    }
    const prev = this.pending;
    if (
      prev &&
      pr.start.t - prev.t <= GESTURE.doubleTapMs &&
      distance(pr.start, prev) <= GESTURE.doubleTapPx
    ) {
      this.pending = null;
      this.chain = {
        side,
        total: GESTURE.seekStep,
        until: p.t + GESTURE.seekChainMs,
      };
      return [{ kind: "seek", side, total: GESTURE.seekStep }];
    }
    // The previous tap did not become a double tap: it counts as one tap now.
    this.pending = { x: pr.start.x, y: pr.start.y, t: p.t };
    return prev ? [{ kind: "tap" }] : [];
  }

  /**
   * Whether the finger about to lift could be a tap (not a long press, not a
   * move). Call before `up()`.
   */
  isTapCandidate(p: GesturePoint): boolean {
    const pr = this.press;
    return (
      pr !== null &&
      !pr.held &&
      !pr.moved &&
      distance(p, pr.start) <= GESTURE.moveSlopPx
    );
  }

  cancel(): GestureOutput[] {
    const pr = this.press;
    this.press = null;
    return pr?.held ? [{ kind: "holdEnd" }] : [];
  }

  /** Whether a tap is waiting for a second one. */
  get hasPending(): boolean {
    return this.pending !== null;
  }

  /** When to call `tick` next (null when nothing waits). */
  deadline(): number | null {
    const pr = this.press;
    const times: number[] = [];
    if (pr && !pr.moved && !pr.held)
      times.push(pr.start.t + GESTURE.longPressMs);
    if (!pr && this.pending) times.push(this.pending.t + GESTURE.doubleTapMs);
    if (!pr && this.chain) times.push(this.chain.until);
    return times.length > 0 ? Math.min(...times) : null;
  }

  tick(t: number): GestureOutput[] {
    const out: GestureOutput[] = [];
    const pr = this.press;
    if (pr && !pr.moved && !pr.held && t >= pr.start.t + GESTURE.longPressMs) {
      pr.held = true;
      // A long press makes the tap before it not count (it led into pressing again).
      this.pending = null;
      this.chain = null;
      out.push({ kind: "holdStart" });
    }
    if (!pr && this.pending && t >= this.pending.t + GESTURE.doubleTapMs) {
      this.pending = null;
      out.push({ kind: "tap" });
    }
    if (!pr && this.chain && t >= this.chain.until) this.chain = null;
    return out;
  }
}

function distance(a: { x: number; y: number }, b: { x: number; y: number }) {
  return Math.hypot(a.x - b.x, a.y - b.y);
}

export interface ViewRect {
  left: number;
  top: number;
  width: number;
  height: number;
}

/**
 * Device coordinates to visual coordinates. `rotated` means the video is
 * turned **90° clockwise** (watched with the device tipped to the left): the
 * video's top-left is at the device's top-right.
 */
export function toViewPoint(
  clientX: number,
  clientY: number,
  rect: ViewRect,
  rotated: boolean,
): { x: number; y: number; width: number; height: number } {
  if (!rotated)
    return {
      x: clientX - rect.left,
      y: clientY - rect.top,
      width: rect.width,
      height: rect.height,
    };
  return {
    x: clientY - rect.top,
    y: rect.left + rect.width - clientX,
    width: rect.height,
    height: rect.width,
  };
}
